import React, { useEffect, useRef, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Globe, ChevronDown, Check } from 'lucide-react';
import { SUPPORTED_LOCALES, getTranslations, isValidLocale } from '../i18n';

const labels = {
  es: { short: 'ES', full: 'Español', flag: 'MX' },
  en: { short: 'EN', full: 'English', flag: 'US' },
};

function findRouteKey(locale, slug) {
  const t = getTranslations(locale);
  for (const [key, val] of Object.entries(t.routes)) {
    if (val === slug) return key;
  }
  return null;
}

export default function LanguageSwitcher() {
  const { lang } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const currentLocale = lang && isValidLocale(lang) ? lang : 'es';
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function switchTo(newLocale) {
    if (newLocale === currentLocale) { setOpen(false); return; }
    const pathAfter = location.pathname.replace(`/${currentLocale}`, '').replace(/^\//, '');
    const routeKey = findRouteKey(currentLocale, pathAfter);
    if (routeKey) {
      const newSlug = getTranslations(newLocale).routes[routeKey];
      navigate(`/${newLocale}${newSlug ? `/${newSlug}` : ''}`);
    } else {
      navigate(`/${newLocale}`);
    }
    setOpen(false);
  }

  const current = labels[currentLocale];

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        data-testid="lang-switcher-btn"
        className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-sm font-medium text-charcoal-600 hover:text-cmic-600 hover:bg-cmic-50 transition-all"
        aria-expanded={open}
      >
        <Globe size={16} className="text-charcoal-400" />
        <span className="font-semibold">{current.short}</span>
        <ChevronDown size={14} className={`text-charcoal-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-1.5 z-50">
          {SUPPORTED_LOCALES.map((loc) => {
            const label = labels[loc];
            const active = loc === currentLocale;
            return (
              <button
                key={loc}
                onClick={() => switchTo(loc)}
                data-testid={`lang-option-${loc}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                  active ? 'bg-cmic-50 text-cmic-700' : 'text-charcoal-600 hover:bg-gray-50'
                }`}
              >
                <span className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-charcoal-500 flex-shrink-0">
                  {label.short}
                </span>
                <div className="flex-1 min-w-0">
                  <span className="block text-sm font-medium">{label.full}</span>
                  <span className="block text-[11px] text-charcoal-400">{label.flag}</span>
                </div>
                {active && <Check size={16} className="text-cmic-500" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
